import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { CategoryOptionFilter, CategoryRangeFilter } from 'graphql-ts-types';
import type {
  AlignItemsProps,
  DisplayProps,
  FlexProps,
  MinWidthProps,
  WidthProps,
} from 'styled-system';
import { alignItems, display, flex, minWidth, width } from 'styled-system';

import type { SortParams } from '../../types/SortAndFilter';
import RangeDisplay from '../RangeDisplay';
import { BodyRegularBR2, HeadlineH8 } from '../Typography';

export const enum FilterSectionTitleKind {
  Sort,
  Range,
  Option,
  Grouped,
}

interface FilterSectionTitleParams {
  kind: FilterSectionTitleKind;
  name: string;
}

type RangeParams = Pick<
  CategoryRangeFilter,
  'min' | 'max' | 'selectedMin' | 'selectedMax' | 'unit'
>;

type OptionsParams = Pick<CategoryOptionFilter, 'multiselect' | 'showIcon' | 'options'>;

type GroupedParams = {
  appliedValues: string | null;
};

type SortTitleParams = FilterSectionTitleParams & SortParams;
type RangeTitleParams = FilterSectionTitleParams & RangeParams;
type OptionsTitleParams = FilterSectionTitleParams & OptionsParams;
type GroupedTitleParams = FilterSectionTitleParams & GroupedParams;

const TitleWrapper = styled(HeadlineH8)<
  DisplayProps & AlignItemsProps & WidthProps & MinWidthProps
>`
  ${display};
  ${alignItems};
  ${width};
  ${minWidth};
`;

const SelectedFilterWrapper = styled(BodyRegularBR2)<FlexProps & MinWidthProps>`
  ${flex};
  ${minWidth};
  max-width: 100%;
`;

const titleWrapperStyles: ComponentProps<typeof TitleWrapper> = {
  display: 'flex',
  alignItems: 'baseline',
  width: 1,
  minWidth: 0,
};

const selectedFilterWrapperStyles: ComponentProps<typeof SelectedFilterWrapper> = {
  flex: 1,
  minWidth: 0,
  color: 'sys.primary.text.default',
  fontWeight: 'normal',
  ml: 'sp_8',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const getSelectedSort = ({ order, sortOptions }: SortParams): string | null => {
  const option = sortOptions.find(sortOption => sortOption.option === order);

  return option && option.localized;
};

const getSelectedOptions = ({ options }: OptionsParams): ReactNode =>
  options
    .filter(option => option.selected)
    .map(option => option.value)
    .join(' | ') || null;

type FilterSectionTitleProps =
  | SortTitleParams
  | RangeTitleParams
  | OptionsTitleParams
  | GroupedTitleParams;

const FilterSectionTitle = (props: FilterSectionTitleProps & { 'data-testid'?: string }) => {
  const { kind, name, 'data-testid': testId, ...rest } = props;

  return (
    <TitleWrapper {...titleWrapperStyles} data-testid={testId}>
      {name}{' '}
      <SelectedFilterWrapper {...selectedFilterWrapperStyles} aria-hidden="true">
        {kind === FilterSectionTitleKind.Sort ? (
          getSelectedSort(rest as SortParams)
        ) : kind === FilterSectionTitleKind.Option ? (
          getSelectedOptions(rest as OptionsParams)
        ) : kind === FilterSectionTitleKind.Grouped ? (
          (rest as GroupedParams).appliedValues
        ) : (
          <RangeDisplay {...(rest as RangeParams)} />
        )}
      </SelectedFilterWrapper>
    </TitleWrapper>
  );
};

export default FilterSectionTitle;
